import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import { GlobalApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import {
  DEFAULT_STUDIO_OPERATIONS_SETTINGS,
  DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED,
  normalizeStudioOperationsSettings,
  OPERATIONS_LOW_ENROLLMENT_THRESHOLD_KEY,
  OPERATIONS_SETTINGS_GROUP,
  OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX_KEY,
  OPERATIONS_TEACHER_LOAD_LIGHT_MAX_KEY,
  OPERATIONS_TEACHER_LOAD_PART_TIME_MAX_KEY,
  parseOperationsThreshold,
  type StudioOperationsSettings,
  type StudioOperationsSettingsPersisted,
} from "./operations-settings-defaults";

export type StudioOperationsSettingsResult = StudioOperationsSettings & {
  persisted: StudioOperationsSettingsPersisted;
};

function createGlobalApi(accessToken?: string): GlobalApi {
  if (accessToken) {
    return createAuthenticatedApi(GlobalApi, accessToken);
  }
  return new GlobalApi(createOpenApiConfiguration());
}

function isNotFoundOrBadRequest(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 400)
  );
}

function settingsMapFromRows(
  rows: { key: string; value: string }[],
): Map<string, string> {
  return new Map(rows.map((row) => [row.key, row.value]));
}

export async function fetchStudioOperationsSettings(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<StudioOperationsSettingsResult> {
  const api = createGlobalApi(options?.accessToken);

  try {
    const rows = await api.apiGlobalSettingsByGroupGet({
      group: OPERATIONS_SETTINGS_GROUP,
      xTenantID: tenantId,
    });
    const map = settingsMapFromRows(rows);

    const settings = normalizeStudioOperationsSettings({
      lowEnrollmentThreshold: parseOperationsThreshold(
        map.get(OPERATIONS_LOW_ENROLLMENT_THRESHOLD_KEY),
        DEFAULT_STUDIO_OPERATIONS_SETTINGS.lowEnrollmentThreshold,
      ),
      teacherLoadLightMax: parseOperationsThreshold(
        map.get(OPERATIONS_TEACHER_LOAD_LIGHT_MAX_KEY),
        DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadLightMax,
      ),
      teacherLoadPartTimeMax: parseOperationsThreshold(
        map.get(OPERATIONS_TEACHER_LOAD_PART_TIME_MAX_KEY),
        DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadPartTimeMax,
      ),
      teacherLoadFullTimeMax: parseOperationsThreshold(
        map.get(OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX_KEY),
        DEFAULT_STUDIO_OPERATIONS_SETTINGS.teacherLoadFullTimeMax,
      ),
    });

    return {
      ...settings,
      persisted: {
        lowEnrollmentThreshold:
          map.get(OPERATIONS_LOW_ENROLLMENT_THRESHOLD_KEY) != null,
        teacherLoadLightMax:
          map.get(OPERATIONS_TEACHER_LOAD_LIGHT_MAX_KEY) != null,
        teacherLoadPartTimeMax:
          map.get(OPERATIONS_TEACHER_LOAD_PART_TIME_MAX_KEY) != null,
        teacherLoadFullTimeMax:
          map.get(OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX_KEY) != null,
      },
    };
  } catch (error) {
    if (!isNotFoundOrBadRequest(error)) {
      // Fall through to defaults when group is missing.
    }
    return {
      ...DEFAULT_STUDIO_OPERATIONS_SETTINGS,
      persisted: { ...DEFAULT_STUDIO_OPERATIONS_SETTINGS_PERSISTED },
    };
  }
}

async function upsertOperationsSetting(
  tenantId: number,
  key: string,
  value: string,
  accessToken: string,
  persisted: boolean,
  errorLabel: string,
): Promise<void> {
  const api = createAuthenticatedApi(GlobalApi, accessToken);

  const createRequest = {
    xTenantID: tenantId,
    createSettingRequest: {
      key,
      value,
      group: OPERATIONS_SETTINGS_GROUP,
    },
  };
  const updateRequest = {
    xTenantID: tenantId,
    updateSettingRequest: {
      key,
      value,
      group: OPERATIONS_SETTINGS_GROUP,
    },
  };

  const throwUpsertError = async (error: unknown, action: "create" | "update") => {
    const message = await getApiErrorMessage(
      error,
      `Failed to ${action} ${errorLabel}`,
    );
    throw new Error(message);
  };

  if (persisted) {
    try {
      await api.apiGlobalSettingsPut(updateRequest);
      return;
    } catch (error) {
      if (!isNotFoundOrBadRequest(error)) {
        await throwUpsertError(error, "update");
      }
    }
  }

  try {
    await api.apiGlobalSettingsPost(createRequest);
    return;
  } catch (error) {
    if (!(error instanceof ResponseError && error.response.status === 409)) {
      await throwUpsertError(error, "create");
    }
  }

  try {
    await api.apiGlobalSettingsPut(updateRequest);
  } catch (error) {
    await throwUpsertError(error, "update");
  }
}

export async function upsertStudioOperationsSettings(
  tenantId: number,
  request: StudioOperationsSettings & {
    persisted: StudioOperationsSettingsPersisted;
  },
  accessToken: string,
): Promise<StudioOperationsSettingsResult> {
  const normalized = normalizeStudioOperationsSettings(request);

  await Promise.all([
    upsertOperationsSetting(
      tenantId,
      OPERATIONS_LOW_ENROLLMENT_THRESHOLD_KEY,
      String(normalized.lowEnrollmentThreshold),
      accessToken,
      request.persisted.lowEnrollmentThreshold,
      "low enrollment threshold setting",
    ),
    upsertOperationsSetting(
      tenantId,
      OPERATIONS_TEACHER_LOAD_LIGHT_MAX_KEY,
      String(normalized.teacherLoadLightMax),
      accessToken,
      request.persisted.teacherLoadLightMax,
      "teacher load light max setting",
    ),
    upsertOperationsSetting(
      tenantId,
      OPERATIONS_TEACHER_LOAD_PART_TIME_MAX_KEY,
      String(normalized.teacherLoadPartTimeMax),
      accessToken,
      request.persisted.teacherLoadPartTimeMax,
      "teacher load part-time max setting",
    ),
    upsertOperationsSetting(
      tenantId,
      OPERATIONS_TEACHER_LOAD_FULL_TIME_MAX_KEY,
      String(normalized.teacherLoadFullTimeMax),
      accessToken,
      request.persisted.teacherLoadFullTimeMax,
      "teacher load full-time max setting",
    ),
  ]);

  return {
    ...normalized,
    persisted: {
      lowEnrollmentThreshold: true,
      teacherLoadLightMax: true,
      teacherLoadPartTimeMax: true,
      teacherLoadFullTimeMax: true,
    },
  };
}
